import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next'; // ייבוא
import { BlogPost } from '../entities/BlogPost';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { he, enUS } from 'date-fns/locale'; // ייבוא לוקאל אנגלי
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Share2, Clock, Eye } from 'lucide-react';
import CommentSection from '../components/blog/commentSection.jsx';
import NewsletterSignup from '../components/blog/newsletterSignup.jsx';
import { useParams } from 'react-router-dom';
import FeaturedPost from '../components/blog/featuredPost';
import { embedMedia } from '../utils/postContentUtils.js';


export default function PostPage() {
    const { t, i18n } = useTranslation(); // הפעלת ה-hook
    const [post, setPost] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [featuredPosts, setFeaturedPosts] = useState([]);
    const { id: postId } = useParams();

    const dateLocale = i18n.language.startsWith('he') ? he : enUS;

    useEffect(() => {
        loadPost();
        loadFeaturedPosts();
    }, [postId, i18n.language]); // הוספת תלות בשפה

    const loadPost = async () => {
        if (!postId) return;
        setIsLoading(true);
        try {
            const fetchedPost = await BlogPost.get(postId);
            setPost(fetchedPost);
        } catch (error) {
            console.error('Error loading post:', error);
            setPost(null);
        }
        setIsLoading(false);
    };

    const loadFeaturedPosts = async () => {
        // טעינת פוסטים מומלצים בשפה הנכונה
        const allPosts = await BlogPost.list(i18n.language);
        setFeaturedPosts(allPosts.filter(p => p.isFeatured && p.id !== postId));
    };

    if (isLoading) {
        return (
            <div className="container mx-auto px-4 py-24 flex flex-col items-center justify-center min-h-[40vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#232946] mb-6"></div>
                <div className="text-2xl text-[#232946] font-bold">{t('pages.post_page.loading')}</div>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="container mx-auto px-4 py-12">
                <div className="max-w-2xl mx-auto text-center">
                    <h1 className="text-3xl font-bold mb-4">{t('pages.post_page.not_found_title')}</h1>
                    <p className="text-gray-600 mb-8">{t('pages.post_page.not_found_desc')}</p>
                    <Button onClick={() => window.history.back()}>{t('pages.post_page.back_button')}</Button>
                </div>
            </div>
        );
    }

    return (
        // אין כאן יותר <header>! הוא מגיע מה-Layout הראשי
        <article className="min-h-screen">
            {/* Hero Image */}
            <div className="relative h-[60vh] min-h-[400px] overflow-hidden">
                <img
                    src={
                        post.imageUrl?.trim()
                            ? post.imageUrl
                            : 'https://images.unsplash.com/photo-1516414447565-b14be0adf13e?auto=format&fit=crop&q=80'
                    }
                    alt={post.title}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent" />
                <div className="absolute bottom-0 right-0 left-0 container mx-auto px-4 py-12">
                    <div className="max-w-3xl">
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap items-center gap-4 mb-6">
                            <Badge className="bg-white/90 text-[#232946]">{t(`categories.${post.category}`)}</Badge>
                            {post.createdAt && (
                                <div className="flex items-center gap-1 text-[#8890b3] text-sm">
                                    <Clock className="w-4 h-4" />
                                    <time dateTime={post.createdAt}>{format(new Date(post.createdAt), 'dd MMMM yyyy', { locale: dateLocale })}</time>
                                </div>
                            )}
                            <div className="flex items-center gap-2 text-white/80">
                                <Eye className="w-4 h-4" />
                                <span>{post.views} {t('post.views')}</span>
                            </div>
                        </motion.div>
                        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-4xl md:text-5xl font-bold text-white mb-6">
                            {post.title}
                        </motion.h1>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="container mx-auto px-4 py-12">
                <div className="flex flex-col lg:flex-row gap-12">
                    <div className="flex-1 max-w-3xl">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="prose prose-lg max-w-none"
                            // --- הוספת שורה זו ---
                            dir={post.language === 'he' ? 'rtl' : 'ltr'}
                            dangerouslySetInnerHTML={{ __html: embedMedia(post.content) }}
                        />
                        <div className="mt-12 border-t pt-12">
                            {post && <CommentSection postId={postId} />}
                        </div>
                    </div>
                    <div className="lg:w-80 flex flex-col gap-8">
                        <div className="sticky top-24 space-y-8">
                            <Button variant="outline" className="w-full" onClick={() => navigator.share({ title: post.title, text: post.excerpt, url: window.location.href, })}>
                                <Share2 className="w-4 h-4 ml-2" />
                                {t('pages.post_page.share_button')}
                            </Button>
                            <NewsletterSignup compact={false} />
                            {featuredPosts.length > 0 && (
                                <div className="bg-[#faf9fa] border border-[#eebbc3] rounded-2xl shadow px-4 py-6">
                                    <h3 className="text-lg font-bold text-[#232946] mb-4 border-b pb-2 border-[#eebbc3]/30">
                                        {t('pages.home.featured_posts')}
                                    </h3>
                                    <div className="space-y-4">
                                        {featuredPosts.map(p => (<FeaturedPost key={p.id} post={p} small />))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </article>
    );
}